import {
  Actions,
  Box,
  Button,
  ContentBlock,
  Heading,
  Stack,
  Text,
  TextField,
  Textarea,
} from 'braid-design-system';
import React from 'react';

import type { ContactSubmission } from '#shared/types';
import { submitContact } from '#src/api/contact';
import { PageWrapper } from '#src/components/PageWrapper/PageWrapper';

const HONEYPOT_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '-9999px',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
};

export const Contact = () => {
  const formRenderedAt = React.useRef(Date.now());
  const [name, setName] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [honeypot, setHoneypot] = React.useState('');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSubmitting(true);

    try {
      const submission: ContactSubmission = {
        name: name || undefined,
        message: message || undefined,
        websiteUrl: honeypot || undefined,
        formRenderedAt: formRenderedAt.current,
      };

      await submitContact(submission);
      setSuccess(true);
    } catch (err) {
      if (err && typeof err === 'object' && 'errors' in err) {
        setErrors((err as { errors: Record<string, string> }).errors);
      } else if (err && typeof err === 'object' && 'error' in err) {
        setErrors({
          _form: String((err as { error: string }).error),
        });
      } else {
        setErrors({ _form: 'Something went wrong. Please try again.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <PageWrapper>
        <ContentBlock>
          <Stack space="large">
            <Heading level="1">Thank you!</Heading>
            <Text>
              Your message has been sent to the PFA. We&apos;ll be in touch if a
              reply is needed.
            </Text>
          </Stack>
        </ContentBlock>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentBlock>
        <Stack space="large">
          <Stack space="medium">
            <Heading level="1">Contact the PFA</Heading>
            <Text>
              We&apos;d love to hear from you regarding comments, feedback or
              suggestions about any PFA activities. We even just love a good
              chat!
            </Text>
          </Stack>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack space="medium">
              {errors._form ? (
                <Text tone="critical">{errors._form}</Text>
              ) : null}

              <TextField
                id="contact-name"
                label="Your name"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                message={errors.name}
                tone={errors.name ? 'critical' : undefined}
              />

              <Textarea
                id="contact-message"
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.currentTarget.value)}
                message={errors.message}
                tone={errors.message ? 'critical' : undefined}
              />

              <Box style={HONEYPOT_STYLE} aria-hidden="true">
                <label htmlFor="contact-honeypot">Website URL</label>
                <input
                  id="contact-honeypot"
                  type="text"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.currentTarget.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </Box>

              <Actions>
                <Button type="submit" loading={submitting} tone="formAccent">
                  Send message
                </Button>
              </Actions>
            </Stack>
          </Box>
        </Stack>
      </ContentBlock>
    </PageWrapper>
  );
};
